import { rank, route, type Finding } from '@/shared/ui/council'
import type { Coordination, NarrativeGraph } from '../api'

export type { Finding }

/**
 * The war-room council — the window read as decisions about speech.
 *
 * ## Why every finding here is about REACH and not about opinion
 *
 * The temptation in a social console is to report sentiment: "62% negative". That number is a
 * summary of what a model thought about text, it moves when the model is retuned, and nobody
 * can act on it. What a campaign can act on is narrower and harder: which argument is
 * spreading, how fast, whether the same phrasing is being repeated by different accounts, and
 * whether there is a human ready to answer it. So the findings are ranked by ENGAGEMENTS at
 * stake, which is a counted quantity, and sentiment appears only as a qualifier on a
 * narrative that is already moving.
 *
 * ## Two claims this council will not make
 *
 * **It never says a coordination signal is a person, a group or a campaign.** The scan counts
 * repeated strings. `confidence` is capped at 90 server-side for exactly this reason, and the
 * wording here says "the same phrasing", never "the same actor" — the evidence supports the
 * first and not the second, and in a war room the difference is defamation.
 *
 * **It never recommends a post.** Drafting is the council's job in the thread view, and
 * deploying is a named human's, enforced in the database. The action lines here say what to
 * look at and who has to decide, never what to say.
 */
export function advise(g: NarrativeGraph, coordination: Coordination[]): Finding[] {
  const out: Finding[] = []
  const t = g.totals
  const num = (n: number | null | undefined) =>
    n == null ? '—' : Math.round(n).toLocaleString()
  const pct = (n: number | null | undefined) =>
    n == null ? '—' : `${Math.round(n * 100)}%`

  // ── what is above the line, and moving ─────────────────────────────────────
  const hot = g.threads
    .filter((x) => x.threat != null && x.threat >= g.deployThreshold)
    .sort((a, b) => (b.velocity ?? 0) - (a.velocity ?? 0))
  if (hot.length > 0) {
    const reach = hot.reduce((n, x) => n + (x.engagements ?? 0), 0)
    const unanswered = hot.filter((x) => x.draftCount === 0 && x.deployedCount === 0)
    out.push({
      id: 'hot',
      severity: unanswered.length > 0 ? 'critical' : 'serious',
      headline: `${hot.length} ${hot.length === 1 ? 'thread is' : 'threads are'} above the response threshold`
        + (unanswered.length > 0 ? `, and ${unanswered.length} of them ${unanswered.length === 1 ? 'has' : 'have'} nothing drafted.` : ', all with a draft in hand.'),
      evidence: `Threshold ${g.deployThreshold}/10. Fastest: ${hot.slice(0, 3)
        .map((x) => `${x.platformCode} ${x.velocity?.toFixed(1) ?? '—'}/min (threat ${x.threat})`)
        .join(' · ')}. ${num(reach)} engagements across all of them.`,
      action: unanswered.length > 0
        ? 'Ask the council for drafts on the unanswered ones first — velocity is the reason, not the threat score. A slower thread at the same score can wait an hour; a fast one cannot, because by the time a reply is approved the audience has moved on.'
        : 'Every one of these has a draft waiting. The bottleneck is now approval, and nothing posts without a named human — go to the queue.',
      votesAtStake: reach,
      cellCodes: hot.slice(0, 6).map((x) => x.platformCode),
    })
  }

  // ── the blind spot ─────────────────────────────────────────────────────────
  // Unscored is NOT low threat. It is the count of threads nothing has read.
  if (t.unscored > 0) {
    const share = t.threads > 0 ? t.unscored / t.threads : 0
    out.push({
      id: 'unscored',
      severity: share > 0.4 ? 'serious' : 'watch',
      headline: `${num(t.unscored)} threads in this window have never been read.`,
      evidence: `${pct(share)} of ${num(t.threads)} threads in the window carry no threat score.`,
      action: 'An unread thread is not a harmless one — it is one nobody has looked at, and the board cannot distinguish the two. Every chart on this page that filters by threat band silently excludes these, so treat the totals as a floor rather than a measurement.',
      votesAtStake: 0,
    })
  }

  // ── repeated phrasing ──────────────────────────────────────────────────────
  const live = coordination.filter((c) => !c.stoodDownAt)
  if (live.length > 0) {
    const worst = [...live].sort((a, b) => b.confidence - a.confidence)[0]!
    out.push({
      id: 'coordination',
      severity: worst.confidence >= 70 ? 'serious' : 'watch',
      headline: `${live.length} ${live.length === 1 ? 'phrase is' : 'phrases are'} being repeated across separate threads.`,
      evidence: `Strongest: "${worst.phrase}" — ${worst.distinctAuthors} distinct authors across `
        + `${worst.threadCount} threads in ${worst.windowMinutes} minutes, confidence ${worst.confidence}/100.`,
      action: 'This counts strings, not intent. It is evidence that the same wording is circulating and it is NOT evidence of who is circulating it — do not name an actor on this alone. What it is good for is deciding whether to answer the phrase itself rather than each thread carrying it.',
      votesAtStake: 0,
      cellCodes: live.slice(0, 4).map((c) => c.phrase.slice(0, 28)),
    })
  }

  // ── the narratives, ranked by movement ─────────────────────────────────────
  /*
     Narratives nothing has read are excluded from this finding, and that is not tidying.

     The server buckets unscored threads into a residue narrative ("Not yet read"). It is
     always the biggest and usually the fastest, because it holds everything nobody has
     looked at — so ranking by velocity without this filter produces the sentence "'Not yet
     read' is the fastest-moving argument about X", which names a placeholder as an argument
     and tells the reader to go and answer it. A narrative with no threat reading on any of
     its threads has not been characterised, and the council cannot say what it is about.
     The unread count is already its own finding, above.
  */
  const characterised = g.narratives.filter((n) => n.threatMax != null)
  const moving = [...characterised].sort((a, b) => b.velocity - a.velocity)
  const top = moving[0]
  if (top && top.velocity > 0) {
    out.push({
      id: 'narrative',
      severity: (top.threatMax ?? 0) >= g.deployThreshold ? 'serious' : 'watch',
      headline: `"${top.label}" is the fastest-moving argument about ${top.subjectName ?? 'a subject you watch'}.`,
      evidence: `${top.threads} threads, ${num(top.engagements)} engagements, ${top.velocity.toFixed(1)}/min`
        + (top.threatMax != null ? `, worst thread ${top.threatMax}/10` : '')
        + `. ${top.kind === 'COORDINATED' ? `Matched by the astroturf scan at ${top.coordinationConfidence ?? '—'}/100.` : 'No repeated phrasing matched — this is organic spread.'}`,
      action: top.kind === 'COORDINATED'
        ? 'Answer the argument once, in the place it is loudest, rather than thread by thread. Repeated phrasing means the same reply lands against most of them.'
        : 'Organic spread means each thread has its own audience, so a single reply will not cover it. Decide whether this argument is worth engaging at all before spending drafts on it — amplification is a cost.',
      votesAtStake: top.engagements,
    })
  }

  // ── where the room can and cannot act ──────────────────────────────────────
  const mute = g.platforms.filter((p) => !p.canPost && p.threads > 0)
  if (mute.length > 0) {
    const stranded = mute.reduce((n, p) => n + p.engagements, 0)
    out.push({
      id: 'platforms',
      severity: mute.some((p) => p.aboveThreshold > 0) ? 'serious' : 'watch',
      headline: `${num(stranded)} engagements sit on platforms this room can watch but cannot reply on.`,
      evidence: mute.map((p) => `${p.label} (${p.threads} threads${p.aboveThreshold > 0 ? `, ${p.aboveThreshold} above threshold` : ''})`).join(' · '),
      action: 'No reply API exists for these, so the answer has to go somewhere else — an owned account, a friendly voice, or a channel outside social entirely. Waiting for the drafting queue to solve it will not work, and the drafts will sit unapproved because there is nowhere to send them.',
      votesAtStake: stranded,
      cellCodes: mute.map((p) => p.label),
    })
  }

  // ── the approval bottleneck ────────────────────────────────────────────────
  if (t.draftsAwaiting > 0) {
    out.push({
      id: 'queue',
      severity: t.draftsAwaiting > 10 ? 'serious' : 'watch',
      headline: `${num(t.draftsAwaiting)} drafts are waiting on a human.`,
      evidence: `${num(t.deployedToday)} deployed today.`,
      action: 'Nothing in this platform can approve these but a person, by design — the requirement is enforced in the database and there is deliberately no bulk approve. If the queue is the constraint, the fix is another approver, not another draft.',
      votesAtStake: 0,
    })
  }

  // ── the subject taking the most of it ──────────────────────────────────────
  const hostile = [...g.subjects]
    .filter((s) => s.threads > 0)
    .sort((a, b) => b.hostileShare * b.engagements - a.hostileShare * a.engagements)[0]
  if (hostile && hostile.hostileShare > 0.4) {
    out.push({
      id: 'subject',
      severity: hostile.hostileShare > 0.65 ? 'serious' : 'watch',
      headline: `${pct(hostile.hostileShare)} of what is said about ${hostile.name ?? 'your most-discussed subject'} is hostile.`,
      evidence: `${hostile.threads} threads across ${hostile.platforms.length} platforms, `
        + `${num(hostile.engagements)} engagements`
        + (hostile.threatMean != null ? `, mean threat ${hostile.threatMean.toFixed(1)}/10` : '') + '.',
      action: 'A hostile share is a reading of tone, not of reach, so do not plan off it alone — check it against the velocity chart. A subject that is 80% hostile and barely moving is a smaller problem than one at 40% that is spreading.',
      votesAtStake: hostile.engagements,
    })
  }

  if (out.length === 0 && t.threads > 0) {
    out.push({
      id: 'quiet',
      severity: 'good',
      headline: 'Nothing in this window crosses a threshold.',
      evidence: `${num(t.threads)} threads, ${num(t.engagements)} engagements, none above ${g.deployThreshold}/10.`,
      action: 'No action. Widen the window if you are checking whether something has been building — a quiet day inside a loud week looks identical to a quiet week from here.',
      votesAtStake: 0,
    })
  }

  // The blind spot ranks above everything, because it qualifies every other finding on the
  // board: a chart that silently excludes half the window is not a smaller measurement, it is
  // a different one.
  const blind = out.find((f) => f.id === 'unscored')
  const rest = rank(out.filter((f) => f.id !== 'unscored'))
  return blind ? [blind, ...rest] : rest
}

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'hot', words: ['urgent', 'now', 'worst', 'threat', 'dangerous', 'respond', 'answer', 'fire', 'priorit', 'what should'] },
  { id: 'narrative', words: ['narrative', 'argument', 'story', 'spreading', 'viral', 'fastest', 'trend'] },
  { id: 'coordination', words: ['coordinat', 'astroturf', 'bot', 'fake', 'organis', 'campaign against', 'same phrase', 'repeated'] },
  { id: 'unscored', words: ['unscored', 'unread', 'blind', 'missing', 'not read', 'coverage', 'trust'] },
  { id: 'platforms', words: ['platform', 'twitter', ' x ', 'facebook', 'tiktok', 'instagram', 'youtube', 'where'] },
  { id: 'queue', words: ['queue', 'approve', 'draft', 'waiting', 'backlog', 'publish', 'post'] },
  { id: 'subject', words: ['subject', 'about me', 'hostile', 'negative', 'sentiment', 'attacking'] },
  { id: 'quiet', words: ['quiet', 'anything', 'all clear', 'ok', 'fine'] },
]

/**
 * The instant reading, shown beside the advisor's considered one.
 *
 * This used to BE the answer, and returning "no rule covers that" for anything outside the
 * keyword table read as a refusal rather than as the absence of a request. Now it is the fast
 * half of a pair: null simply means the rules have nothing to add, and the card shows only
 * what the council returns.
 */
export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}
